import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Badge, Row, Col, Card } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiPrinter } from 'react-icons/fi';
import api from '../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Reports = () => {
    const { id } = useParams();
    const [reportData, setReportData] = useState([]);
    const [training, setTraining] = useState(null);

    useEffect(() => {
        fetchReport();
        fetchTraining();
    }, [id]);

    const fetchTraining = async () => {
        try {
            const res = await api.get(`/trainings/${id}`);
            setTraining(res.data);
        } catch (err) {
            console.error("Erro ao buscar detalhes do treinamento", err);
        }
    };

    const fetchReport = async () => {
        try {
            const res = await api.get(`/reports/${id}`);
            setReportData(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const getBase64ImageFromURL = (url) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.setAttribute("crossOrigin", "anonymous");
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0);
                const dataURL = canvas.toDataURL("image/png");
                resolve(dataURL);
            };
            img.onerror = error => reject(error);
            img.src = url;
        });
    };

    const generatePDF = async () => {
        if (!training || reportData.length === 0) {
            alert("Dados insuficientes para gerar o relatório.");
            return;
        }

        let logoBase64 = null;
        try {
            logoBase64 = await getBase64ImageFromURL('/logo_texto_lateral.png');
        } catch (e) {
            console.error("Erro ao carregar logo", e);
        }

        const doc = new jsPDF();
        
        // Brand Colors (RGB)
        const colorPrimary = [0, 191, 165]; // Turquesa Digital #00BFA5
        const colorContrast = [0, 96, 100]; // Azul Petróleo #006064
        const colorLightGray = [245, 247, 250]; // Cinza Claro #F5F7FA
        const colorText = [60, 60, 60];

        const totalParticipants = reportData.length;
        const presentParticipants = reportData.filter(p => p.Status === 'Presente').length;
        const participationRate = totalParticipants > 0 
            ? Math.round((presentParticipants / totalParticipants) * 100) 
            : 0;

        // --- Header ---
        // Header Background
        doc.setFillColor(...colorLightGray);
        doc.rect(0, 0, 210, 45, 'F');

        // Brand Name/Logo
        if (logoBase64) {
            const imgProps = doc.getImageProperties(logoBase64);
            const pdfWidth = 50; 
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
            doc.addImage(logoBase64, 'PNG', 20, 10, pdfWidth, pdfHeight);
        } else {
            doc.setFont("helvetica", "bold");
            doc.setFontSize(22);
            doc.setTextColor(...colorPrimary);
            doc.text("Confirma AI", 20, 20);
        }

        // Report Title
        doc.setFontSize(14);
        doc.setTextColor(...colorContrast);
        doc.text("Relatório de Presença", 20, 35);

        // Date generated
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 100, 100);
        doc.text(`Gerado em:`, 190, 20, { align: "right" });
        doc.text(`${new Date().toLocaleDateString()} às ${new Date().toLocaleTimeString()}`, 190, 26, { align: "right" });

        // --- Training Info Section ---
        let yPos = 60;
        
        // Training Title
        doc.setFontSize(16);
        doc.setTextColor(...colorContrast);
        doc.setFont("helvetica", "bold");
        doc.text(training.titulo, 20, yPos);
        
        yPos += 12;
        
        // Info Grid
        doc.setFontSize(10);
        doc.setTextColor(...colorText);
        
        // Column 1
        doc.setFont("helvetica", "bold");
        doc.text("Data:", 20, yPos);
        doc.setFont("helvetica", "normal");
        doc.text(new Date(training.data).toLocaleDateString(), 35, yPos);
        
        doc.setFont("helvetica", "bold");
        doc.text("Hora:", 20, yPos + 6);
        doc.setFont("helvetica", "normal");
        doc.text(new Date(training.data).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), 35, yPos + 6);

        // Column 2
        doc.setFont("helvetica", "bold");
        doc.text("Local:", 80, yPos);
        doc.setFont("helvetica", "normal");
        doc.text(training.local || "Não informado", 95, yPos);

        // Column 3 (Stats Box)
        doc.setDrawColor(...colorPrimary);
        doc.setLineWidth(0.5);
        doc.roundedRect(150, yPos - 8, 40, 20, 2, 2, 'S');
        
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...colorContrast);
        doc.text("Participação", 170, yPos - 2, { align: "center" });
        
        doc.setFontSize(14);
        doc.setTextColor(...colorPrimary);
        doc.text(`${participationRate}%`, 170, yPos + 4, { align: "center" });
        
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(`${presentParticipants}/${totalParticipants} presentes`, 170, yPos + 9, { align: "center" });

        yPos += 20;

        // --- Table ---
        const tableColumn = ["Participante", "CPF", "Turma", "Status"];
        const tableRows = [];

        reportData.forEach(row => {
            tableRows.push([
                row.Nome,
                row.CPF,
                row.Turma || '-',
                row.Status
            ]);
        });

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: yPos,
            theme: 'grid',
            headStyles: { 
                fillColor: colorContrast, 
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                halign: 'left',
                minCellHeight: 12,
                valign: 'middle'
            },
            styles: { 
                fontSize: 10,
                cellPadding: 4,
                textColor: colorText,
                lineColor: [230, 230, 230],
                valign: 'middle'
            },
            alternateRowStyles: {
                fillColor: [250, 250, 250]
            },
            columnStyles: {
                0: { cellWidth: 70 }, // Nome
                1: { cellWidth: 40 }, // CPF
                2: { cellWidth: 40 }, // Turma
                3: { cellWidth: 30, halign: 'center', fontStyle: 'bold' }  // Status
            },
            didParseCell: function(data) {
                if (data.section === 'body' && data.column.index === 3) {
                    if (data.cell.raw === 'Presente') {
                        data.cell.styles.textColor = [33, 150, 83]; // Success Green
                    } else if (data.cell.raw === 'Falta') {
                        data.cell.styles.textColor = [220, 53, 69]; // Danger Red
                    } else {
                        data.cell.styles.textColor = [255, 193, 7]; // Warning Yellow/Orange
                    }
                }
            }
        });

        // --- Footer ---
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            
            doc.setDrawColor(...colorLightGray);
            doc.setLineWidth(1);
            doc.line(20, 280, 190, 280);

            doc.setFontSize(9);
            doc.setTextColor(150, 150, 150);
            doc.text("Confirma AI - Gestão Inteligente de Treinamentos", 20, 288);
            doc.text(`Página ${i} de ${pageCount}`, 190, 288, { align: "right" });
        }

        window.open(doc.output('bloburl'), '_blank');
    };

    return (
        <Container className="mt-4">
            <div className="mb-4">
                <Button 
                    variant="link" 
                    as={Link} 
                    to="/training-list" 
                    className="text-decoration-none p-0 mb-2 text-muted"
                >
                    <FiArrowLeft className="me-1" /> Voltar para Lista de treinamento
                </Button>                
            </div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h2 className="mb-4 fw-bold" style={{ fontFamily: 'Nunito, sans-serif', color: '#006064' }}>Relatório de Presença</h2>
                <div className="d-flex gap-2">
                    <Button variant="primary" onClick={generatePDF}>
                        <FiPrinter className="me-2" /> Imprimir Relatório
                    </Button>
                </div>
            </div>
            {training && (
                <Card className="mb-4 shadow-sm">
                    <Card.Body>
                        <Row>
                            <Col md={6}>
                                <p className="mb-1"><strong>Treinamento:</strong> {training.titulo}</p>
                                <p className="mb-1"><strong>Data:</strong> {new Date(training.data).toLocaleString()}</p>
                            </Col>
                            <Col md={6}>
                                <p className="mb-1"><strong>Local:</strong> {training.local || '-'}</p>
                                <p className="mb-0"><strong>Participação:</strong> {reportData.filter(p => p.Status === 'Presente').length} / {reportData.length}</p>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            <Card className="shadow-sm border-0">
                <Card.Body className="p-0">
                    <Table hover responsive className="mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th className="border-0 text-muted small fw-bold text-uppercase ps-4 py-3">Nome</th>
                                <th className="border-0 text-muted small fw-bold text-uppercase py-3">CPF</th>
                                <th className="border-0 text-muted small fw-bold text-uppercase py-3">Turma</th>
                                <th className="border-0 text-muted small fw-bold text-uppercase py-3">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.map((row, idx) => (
                                <tr key={idx} className="align-middle border-bottom">
                                    <td className="ps-4 py-3 fw-bold text-dark">{row.Nome}</td>
                                    <td className="py-3 text-muted">{row.CPF}</td>
                                    <td className="py-3 text-muted">{row.Turma || '-'}</td>
                                    <td className="py-3">
                                        <Badge bg={row.Status === 'Presente' ? 'success' : row.Status === 'Falta' ? 'danger' : 'warning'} className="fw-normal px-3 py-2 rounded-pill">
                                            {row.Status}
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                            {reportData.length === 0 && (
                                <tr>
                                    <td colSpan="4" className="text-center py-5 text-muted">Nenhum participante encontrado.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default Reports;
